import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { FormControl } from 'react-bootstrap';
import { injectIntl } from 'react-intl';
import withStyles from 'isomorphic-style-loader/lib/withStyles';
import cx from 'classnames';

import messages from '../../../locale/messages';
import s from './AdminAsyncAutocomplete.css';
import cp from '../../../components/commonStyle.css';

class AdminAsyncAutocomplete extends Component {
  static propTypes = {
    input: PropTypes.object.isRequired,
    meta: PropTypes.object,
    loadOptions: PropTypes.func.isRequired,
    placeholder: PropTypes.string,
    inputClass: PropTypes.string,
    minChars: PropTypes.number,
  };

  static defaultProps = {
    minChars: 0,
  };

  constructor(props) {
    super(props);
    this.state = {
      query: '',
      options: [],
      loading: false,
      open: false,
      selectedLabel: '',
      activeIndex: -1,
    };
    this.containerRef = null;
    this.typingTimeout = null;
    this.latestRequestId = 0;
  }

  componentDidMount() {
    document.addEventListener('mousedown', this.handleClickOutside);
  }

  componentDidUpdate(prevProps) {
    const { input } = this.props;
    if (prevProps.input.value && !input.value && this.state.selectedLabel) {
      this.setState({
        query: '',
        selectedLabel: '',
        options: [],
        open: false,
        activeIndex: -1,
      });
    }
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClickOutside);
    if (this.typingTimeout) {
      clearTimeout(this.typingTimeout);
    }
  }

  handleClickOutside = (event) => {
    if (this.containerRef && !this.containerRef.contains(event.target)) {
      this.setState({ open: false, activeIndex: -1 });
    }
  };

  setContainerRef = (node) => {
    this.containerRef = node;
  };

  fetchOptions = async (searchList) => {
    const { loadOptions, minChars } = this.props;
    const trimmed = (searchList || '').trim();

    if (trimmed.length < minChars) {
      this.setState({ options: [], loading: false, open: true, activeIndex: -1 });
      return;
    }

    const requestId = ++this.latestRequestId;
    this.setState({ loading: true, open: true });

    try {
      const options = await loadOptions(trimmed);
      if (requestId !== this.latestRequestId) return;
      this.setState({
        options: Array.isArray(options) ? options : [],
        loading: false,
        activeIndex: -1,
      });
    } catch (error) {
      if (requestId !== this.latestRequestId) return;
      this.setState({ options: [], loading: false, activeIndex: -1 });
    }
  };

  handleFocus = () => {
    const { selectedLabel, query, options } = this.state;
    if (selectedLabel) {
      this.setState({ open: false });
      return;
    }
    if (options.length > 0) {
      this.setState({ open: true });
      return;
    }
    this.fetchOptions(query);
  };

  handleChange = (event) => {
    const { input } = this.props;
    const query = event.target.value;

    if (this.state.selectedLabel) {
      input.onChange('');
    }

    this.setState({
      query,
      selectedLabel: '',
      open: true,
    });

    if (this.typingTimeout) {
      clearTimeout(this.typingTimeout);
    }

    this.typingTimeout = setTimeout(() => {
      this.fetchOptions(query);
    }, 300);
  };

  handleSelect = (option) => {
    const { input } = this.props;
    if (!option) return;

    input.onChange(option.value);
    this.setState({
      selectedLabel: option.label,
      query: '',
      open: false,
      options: [],
      activeIndex: -1,
    });
  };

  handleBlur = () => {
    const { input } = this.props;
    // Pass the stored field value, not the visible label text from the input.
    input.onBlur(input.value);
  };

  handleClear = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const { input } = this.props;
    input.onChange('');
    this.setState({
      query: '',
      selectedLabel: '',
      options: [],
      open: false,
      activeIndex: -1,
    });
  };

  handleKeyDown = (event) => {
    const { open, options, activeIndex } = this.state;
    if (!open || options.length === 0) {
      if (event.key === 'ArrowDown') {
        this.fetchOptions(this.state.query);
      }
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.setState({
        activeIndex: activeIndex < options.length - 1 ? activeIndex + 1 : 0,
      });
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.setState({
        activeIndex: activeIndex > 0 ? activeIndex - 1 : options.length - 1,
      });
    } else if (event.key === 'Enter' && activeIndex >= 0) {
      event.preventDefault();
      this.handleSelect(options[activeIndex]);
    } else if (event.key === 'Escape') {
      this.setState({ open: false, activeIndex: -1 });
    }
  };

  render() {
    const { meta: { touched, error }, placeholder, inputClass, input } = this.props;
    const { formatMessage } = this.props.intl;
    const { query, options, loading, open, selectedLabel, activeIndex } = this.state;
    const displayValue = selectedLabel || query;
    const showClear = Boolean(input.value || selectedLabel || query);

    return (
      <div className={s.root} ref={this.setContainerRef}>
        <div className={s.inputWrap}>
          <FormControl
            type="text"
            value={displayValue}
            placeholder={placeholder}
            onChange={this.handleChange}
            onFocus={this.handleFocus}
            onBlur={this.handleBlur}
            onKeyDown={this.handleKeyDown}
            autoComplete="off"
            className={cx(inputClass || cp.formControlInput, showClear && s.inputWithClear)}
          />
          {showClear && (
            <button
              type="button"
              className={s.clearButton}
              onClick={this.handleClear}
              aria-label="Clear"
            >
              ×
            </button>
          )}
        </div>
        {open && (
          <div className={s.dropdown} role="listbox">
            {loading && (
              <div className={s.status}>Loading...</div>
            )}
            {!loading && options.length === 0 && (
              <div className={s.status}>
                {formatMessage(messages.noRecordFound)}
              </div>
            )}
            {!loading && options.map((option, index) => (
              <button
                type="button"
                key={String(option.value)}
                className={cx(s.option, index === activeIndex && s.optionActive)}
                onMouseDown={(event) => {
                  event.preventDefault();
                  this.handleSelect(option);
                }}
              >
                {option.label}
              </button>
            ))}
          </div>
        )}
        {touched && error && (
          <span className={s.errorMessage}>{formatMessage(error)}</span>
        )}
      </div>
    );
  }
}

export default injectIntl(withStyles(s, cp)(AdminAsyncAutocomplete));
